import { CreditPricePoint, CreditType, Wallet } from "@/models/models";
import { getPricePoints, getWallets } from "@/services/credits";
import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useAuth } from "./AuthContext";
import { auth } from "@/lib/firebaseClient";

export interface ICreditsContext {
  wallets: Wallet[];
  pricePoints: CreditPricePoint[];
  loadWallets: () => Promise<void>;
  loadPricePoints: () => Promise<void>;
  getCreditCountByCreditType: (creditType: CreditType) => number;
  getPricePointsByCreditType: (
    creditType: CreditType | undefined
  ) => CreditPricePoint[];
}

const defaultState: ICreditsContext = {
  wallets: [],
  pricePoints: [],
  loadWallets: async () => { },
  loadPricePoints: async () => { },
  getCreditCountByCreditType: () => 0,
  getPricePointsByCreditType: () => [],
};

const CreditsContext = createContext<ICreditsContext>(defaultState);

const CreditsProvider = (props: { children: ReactNode }) => {
  const { user } = useAuth();
  const [wallets, setWallets] = useState(defaultState.wallets);
  const [pricePoints, setPricePoints] = useState(defaultState.pricePoints);

  const loadWallets = useCallback(async () => {
    if (!auth.currentUser || !user?.email || !user?.id) {
      setWallets([]);
      return;
    }
    try {
      const walletsData = await getWallets(user.email);
      setWallets(walletsData);
    } catch (error: any) {
      if (error?.response?.status === 401) {
        setWallets([]);
        return;
      }
      setWallets([]);
    }
  }, [user]);

  const loadPricePoints = useCallback(async () => {
    setPricePoints(await getPricePoints());
  }, []);

  useEffect(() => {
    if (user && user.email && user.id) {
      loadWallets();
    } else {
      setWallets([]);
    }
  }, [user]);

  useEffect(() => {
    loadPricePoints();
  }, []);

  const getCreditCountByCreditType = (creditType: CreditType): number => {
    const wallet = wallets.find((wallet) => wallet.credit_type == creditType);
    if (wallet === undefined) return 0;
    return wallet.credit_count;
  };

  const getPricePointsByCreditType = (
    creditType: CreditType | undefined
  ): CreditPricePoint[] => {
    let filteredPricePoints = pricePoints;
    if (creditType !== undefined)
      filteredPricePoints = filteredPricePoints.filter(
        (pricePoint) => pricePoint.credit_type === creditType
      );
    return filteredPricePoints;
  };

  return (
    <CreditsContext.Provider
      value={{
        wallets: wallets,
        pricePoints: pricePoints,
        loadWallets: loadWallets,
        loadPricePoints: loadPricePoints,
        getCreditCountByCreditType: getCreditCountByCreditType,
        getPricePointsByCreditType: getPricePointsByCreditType,
      }}
    >
      {props.children}
    </CreditsContext.Provider>
  );
};

const useCredits = () => {
  const context = useContext(CreditsContext);
  if (context === undefined) {
    throw new Error("useWallet must be used within a WalletProvider");
  }
  return context;
};

export { CreditsProvider, useCredits };
